import { PlayerCardDrawings, PlayerCardListBySessionQuery } from '@/API';
import FadeInWrapper from '@/components/Animated/FadeWrapper';
import Canvas from '@/components/Controls/Canvas';
import { Panel } from '@/components/Layouts/ContentPanel';
import * as queries from '@/graphql/queries';
import { useAmplifyApiClient } from '@/hooks/AmplifyApiClientHooks';
import { GraphQLQuery } from 'aws-amplify/api';
import { Stage } from 'konva/lib/Stage';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

export default function CardView() {
  const client = useAmplifyApiClient();
  const { id } = useParams();

  const [games, setGames] = useState<
    {
      index: number;
      prompt: string;
      cards: PlayerCardDrawings[];
    }[]
  >([]);
  const canvasRefs = useRef<Map<string, Stage>>(new Map());

  useEffect(() => {
    const fetchDrawings = async () => {
      if (!id) return;

      const playerCardsResult: { data: PlayerCardListBySessionQuery } = await client.graphql<
        GraphQLQuery<PlayerCardListBySessionQuery>
      >({
        query: queries.playerCardListBySession,
        variables: { sessionId: id }
      });

      const cards = (playerCardsResult.data.playerCardListBySession ?? []) as PlayerCardDrawings[];

      // Group cards by game and sort rounds by gameIndex
      const grouped = new Map<
        string,
        { index: number; prompt: string; cards: PlayerCardDrawings[] }
      >();
      for (const card of cards) {
        const gameId = (card as any).game?.id ?? card.gameId;
        const gameIndex = (card as any).game?.gameIndex ?? 0;
        const promptText = (card as any).game?.promptText ?? '';

        const existing = grouped.get(gameId);
        if (existing) {
          existing.cards.push(card);
        } else {
          grouped.set(gameId, { index: gameIndex, prompt: promptText, cards: [card] });
        }
      }

      const groupedArray = Array.from(grouped.values()).sort((a, b) => a.index - b.index);
      setGames(groupedArray);
    };

    fetchDrawings();
  }, [id, client]);

  const handleDownload = (id: string) => {
    const canvas = canvasRefs.current.get(id.toString());
    if (canvas) {
      const link = document.createElement('a');
      link.download = `card_${id + 1}.png`;
      link.href = canvas.toDataURL({
        mimeType: 'image/png',
        quality: 1
      }) as string;
      link.click();
    }
  };

  return (
    <FadeInWrapper>
      <div className="mt-8">
        <Panel title="Session Cards">
          <div className="w-full">
            {games.map((game, index) => (
              <div key={index} className="flex flex-col items-center">
                <h3 className="text-2xl font-bold mb-2 mt-8">
                  Round {game.index + 1}: <span className="font-normal">{game.prompt}</span>
                </h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                  {game.cards.map((card, index) => (
                    <div key={index} className="flex flex-col items-center justify-center">
                      <Canvas
                        ref={(el) => {
                          canvasRefs.current?.set(card!.id, el!);
                        }}
                        drawing={JSON.parse(card.drawing ?? '[]')}
                        isViewerMode={true}
                      />
                      <h4 className="text-xl font-semibold mt-4">{card.player?.name ?? ''}</h4>
                      <button
                        className="btn btn-primary mt-6"
                        onClick={() => handleDownload(card.id)}
                      >
                        Download
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </FadeInWrapper>
  );
}
